/**
 * MSAL Authentication Configuration for Azure AD / Entra ID
 * 
 * Following the official Microsoft tutorial pattern:
 * https://learn.microsoft.com/en-us/entra/identity-platform/tutorial-single-page-app-react-prepare-app
 * 
 * This file exports ONLY configuration objects.
 * The PublicClientApplication instance is created in index.js and
 * provided to the component tree via <MsalProvider>.
 * Components use the useMsal() hook from @azure/msal-react.
 * 
 * Values are loaded from public/config.js at runtime
 * with public/config.json as fallback.
 */

import { LogLevel } from '@azure/msal-browser';
import { getBasePath } from './appConfig';
import {
  getConfigValue,
  getConfiguredApiScopes,
  getConfiguredLoginScopes,
} from './runtimeConfig';

export const APP_ADMIN_ROLE = 'AppAdmin';

const getAuthority = () => {
  const authorityBaseUrl = getConfigValue('authorityBaseUrl', 'https://login.microsoftonline.com');
  const tenantId = getConfigValue('tenantId', '69b7a791-5a9c-4661-b6cf-1c6fb3d271c6');
  return `${authorityBaseUrl}/${tenantId}`;
};

const getRedirectUri = () => {
  return window.location.origin + getBasePath();
};

/**
 * MSAL Configuration
 * @see https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const getMsalConfig = () => {
  const redirectUri = getRedirectUri();

  return {
    auth: {
      clientId: getConfigValue('clientId', '8ffc435e-c2e8-40e0-9880-761fe4dfa304'),
      authority: getAuthority(),
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error('[MSAL]', message);
              break;
            case LogLevel.Warning:
              console.warn('[MSAL]', message);
              break;
            case LogLevel.Info:
              if (process.env.NODE_ENV === 'development') {
                console.info('[MSAL]', message);
              }
              break;
            default:
              break;
          }
        },
        logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Verbose : LogLevel.Warning,
      },
    },
  };
};

/**
 * Login request for authentication UX (no API token audience guaranteed).
 */
export const getLoginRequest = () => ({
  scopes: getConfiguredLoginScopes(),
});

/**
 * API token request scoped to the backend API resource.
 * This must be used when acquiring access tokens for admin API calls.
 */
export const getApiTokenRequest = () => ({
  scopes: getConfiguredApiScopes(),
});

/**
 * Checks whether token claims include the required AppAdmin role.
 * @param {object|undefined} claims
 * @returns {boolean}
 */
export const hasAppAdminRole = (claims) => {
  const configuredAdminRole = getConfigValue('adminRole', APP_ADMIN_ROLE);
  const roles = claims?.roles;
  return Array.isArray(roles) && roles.includes(configuredAdminRole);
};
